<template>
<div>
        <v-row justify="center" style="background-color: #06599D;">
            <v-col cols="0" sm="2"></v-col>
            <v-col cols="12" sm="8">
                <v-row>
                    <ReportSelection 
                        title="Dell Internal Reports" 
                        filter=":DellInternal:"
                        showDownloadOnly
                        pleaseNote
                        @reportChanged="(value) => selectedReport = value"
                        @downloadOnlyChanged="(value) => downloadOnly = value">
                    </ReportSelection>
                </v-row>
                <v-row v-if="requiresQuarter">
                    <v-select class="text-white" label="Select Quarter" v-model="selectedQuarter"
                    :items="quarters" variant="outlined"></v-select>
                </v-row>
                <v-row>
                    <v-btn @click="getReportData" color="#1e70c7">Submit</v-btn>
                </v-row>
            </v-col>
            <v-col cols="0" sm="2"></v-col>
        </v-row>
        <v-row justify="center" style="background-color: #ffffff;" class="pb-6">
            <v-toolbar density="compact" color="#1d5895">
                <v-spacer></v-spacer>
                <v-btn prepend-icon="mdi-download" @click="downloadFile" aria-label="Download Report"></v-btn>
            </v-toolbar>
            <v-container>
                <div v-if="!isCustomReport">
                    <v-text-field v-model="searchString" label="Filter" prepend-inner-icon="mdi-filter-outline"
                        variant="outlined" hide-details single-line></v-text-field> 
                    <v-data-table items-per-page="50" :headers="headers" :items="reportData"
                        item-key="id" :search="searchString" class="v-data-table">
                        <template v-slot:item.inviteNewUser="{ item }">
                            <v-btn v-if="systemStore.userDetail.isOrganizationAdmin === true && item.tsAndCsAccepted === 'No'" icon="mdi-account-plus" @click="inviteNewUser(item.partnerAffinityId, item.partnerName)" variant="text" />
                        </template>
                    </v-data-table>                    
                </div>
                <div v-if="showCustomReport">
                    <BreakdownReport :report="reportData">
                    </BreakdownReport>
                </div>
            </v-container>
        </v-row>
        <v-dialog v-model="showSendInvitation" width="650" scrollable>
            <v-card>
                <v-toolbar color="#0d2155">
                    <v-toolbar-title>Invite User</v-toolbar-title>
                </v-toolbar>
                <v-card-text class="pa-4">
                    <p class="my-2 general-text">
                        Please complete the form below to invite a new user to join RISE. The email address used must match the email domain for the Organization.
                    </p>
                    <v-text-field readonly v-model="invitationData.affinityId" variant="outlined" label="Affinity Id" append-inner-icon="mdi-pencil-lock">

                    </v-text-field>
                    <v-text-field readonly v-model="invitationData.partnerName" variant="outlined" label="Partner Name" append-inner-icon="mdi-pencil-lock">

                    </v-text-field>
                    <v-text-field :rules="firstnameRules" required v-model="invitationData.firstname" variant="outlined" label="Firstname">

                    </v-text-field>
                    <v-text-field :rules="lastnameRules" required v-model="invitationData.lastname" variant="outlined" label="Lastname">

                    </v-text-field>
                    <v-text-field :rules="emailRules" required v-model="invitationData.emailAddress" variant="outlined" label="Email Address">

                    </v-text-field>
                </v-card-text>
                <v-card-actions class="border-t-thin">
                    <v-btn @click="showSendInvitation = false" width="100">Close</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn :disabled="!canSubmitInvitation" width="100" @click="submitInvitation" color="#055699" variant="elevated">Submit</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useSystemStore } from '@/stores/system'
import { useReportsStore } from '@/stores/reports'
import { useExcelStore } from '@/stores/excel'
import { useRegistrationStore } from '@/stores/registration'
import ReportSelection from '@/components/ReportSelection.vue'
import BreakdownReport from '@/components/BreakdownReport.vue'

const systemStore = useSystemStore()
const reportsStore = useReportsStore()
const excelStore = useExcelStore()
const registrationStore = useRegistrationStore()
const requiresQuarter = ref(false)
const selectedQuarter = ref('')
const isCustomReport = ref(false)
const customBreakdownReportId = 12
const customReportIds = ref([customBreakdownReportId])

const reportData = ref([])
const searchString = ref('')
const currentReport = ref('')
const headers = ref([])
const selectedReport = ref(null)
const downloadOnly = ref(false)
const showSendInvitation = ref(false)
const invitationData = ref({
    affinityId: 0,
    partnerName: '',
    firstname: '',
    lastname: '',
    emailAddress: ''
}) 
const quarters = ref([])

const firstnameRules = [ value => !!value || 'You must enter the First Name.' ]
const lastnameRules = [ value => !!value || 'You must enter the Last Name.' ]
const emailRules = [ value => !!value || 'You must enter a valid email address.',
  value => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value) || 'You must enter an email address.'
 ]

const validateAgainstRules = (value, rules) => {
    for (const rule of rules) {
        const result = rule(value);
        if (result !== true) {
            return false;
        }
    }
    return true; 
};

const downloadFile = () => {
    if (reportData.value.length === 0) {
        return
    }

    if (customReportIds.value.indexOf(selectedReport.value.Id) === -1) {
        excelStore.generateInMemoryExcelDocumentV2(reportData.value, currentReport.value).catch(error => {
            console.log('Generate In Memory Excel Document', error)
        })
    }
    else {
        excelStore.generateInMemoryExcelCustomDocument(reportData.value, currentReport.value).catch(error => {
            console.log('Generate In Memory Excel Custom Document', error)
        })
    }
}

const getReportData = () => {
    reportData.value = []
    currentReport.value = selectedReport.value.ReportTitle

    if (selectedReport.value.Params.length > 0) {   

        selectedReport.value.Params.forEach((p) => {
            if (p.Name === 'Quarter') {
                p.Value = selectedQuarter.value
            }
            else if (p.Name === 'PAMUser') {
                if (systemStore.userDetail.userType.trim() === 'PAM') {            
                    p.Value = `${systemStore.userDetail.firstname.trim()} ${systemStore.userDetail.lastname.trim()}`
                }
            }
        })

    }

    reportData.value = []

    if (customReportIds.value.indexOf(selectedReport.value.Id) === -1) {
        isCustomReport.value = false
    }
    else {
        isCustomReport.value = true
    }

    reportsStore.getReportData(selectedReport.value, downloadOnly.value, isCustomReport.value).then(result => {
    if (result.success === true) {
        if (downloadOnly.value) {     
            excelStore.downloadInMemoryExcelDocument(result.reportArray, currentReport.value).catch(error => {
                console.log('Download In Memory Excel Document', error)
            })                 
        }
        else {
            if (result.hasData) {
                const jsonObject = JSON.parse(result.reportData)
                const headings = Object.keys(jsonObject[0])
                headers.value = []

                if (customReportIds.value.indexOf(selectedReport.value.Id) === -1) {

                    //Invite User Function on PAM Report
                    if (selectedReport.value.Id === 10) {      
                        if (systemStore.userDetail.isOrganizationAdmin) {
                            headers.value.push({
                                title: `Invite New User`,
                                value: `inviteNewUser`,
                                headerProps: { class: 'header-style' },
                                sortable: false
                            });
                        }
                    }

                    headings.forEach(element => {
                        headers.value.push({
                            title: `${reportsStore.formatHeader(element)}`,
                            value: `${element}`,
                            headerProps: { class: 'header-style' },
                            sortable: true
                        });
                    });

                }   
                
                reportData.value = jsonObject;
            }            
        }
    }
  }).catch(error => {
      console.log(`get data`, error)
  })
}

const inviteNewUser = (partnerAffinityId, partnerName) => {
    showSendInvitation.value = true

    invitationData.value.affinityId = partnerAffinityId
    invitationData.value.partnerName = partnerName
    invitationData.value.firstname = ''
    invitationData.value.lastname = ''
    invitationData.value.emailAddress = ''
}

const submitInvitation = () => {
    registrationStore.sendUserInvitation(invitationData.value.affinityId, invitationData.value.partnerName, invitationData.value.firstname,invitationData.value.lastname,invitationData.value.emailAddress).then(result => {
        if (result.success === true){
            reportData.value.forEach((element) => {
                if (element.partnerAffinityId === invitationData.value.affinityId) {
                    element.tsAndCsAccepted = 'Invited'
                }
            })
            showSendInvitation.value = false
        }
    })
}

const canSubmitInvitation = computed(() => {
  if (!validateAgainstRules(invitationData.value.firstname, firstnameRules)) {
    return false
  }  
  if (!validateAgainstRules(invitationData.value.lastname, lastnameRules)) {
    return false
  }
  if (!validateAgainstRules(invitationData.value.emailAddress, emailRules)) {
    return false
  }
  return true
})

watch(
    () => selectedReport.value, (val) => {
        requiresQuarter.value = false

        if (val.Params.length > 0) {   
            val.Params.forEach((p) => {
                if (p.Name === 'Quarter') {
                    requiresQuarter.value = true
                }
            })
        } 
    }
)
  
const showCustomReport = computed(() => {
    return reportData.value.length > 0 && isCustomReport.value === true
});

onMounted(() => {
    systemStore.getQuarters().then(result => {
        if (result.success) {
            quarters.value = result.quarters
                .filter(q => q.currentQuarters === true)
                .map(q => q.quarter)
        }
    }).catch(error => {
        console.log('recalculateLocalValue', error)
    })
})

</script>

<style scoped>

.header-style {

/* background-color: #012a4b; */
background-color: #012a4b;
color: white;
}

.v-data-table tbody tr:nth-child(even) {
  background-color: #e0e6ec;
  color: #202520;
}

.v-data-table tbody tr:nth-child(odd) {
  background-color: whitesmoke;
  color: #202520;
}
</style>